import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './Factoring.css';

const modules = [
  {
    icon: 'fas fa-file-invoice-dollar',
    title: 'Gestión integral de cesiones',
    description:
      'Administre solicitudes, formalización y cesión de facturas con flujos parametrizables y alertas automatizadas.'
  },
  {
    icon: 'fas fa-shield-alt',
    title: 'Seguridad administrada',
    description:
      'Integre Microsoft Intune, Kerio Control y GFI LanGuard para proteger dispositivos, redes y vulnerabilidades en una sola consola.'
  },
  {
    icon: 'fas fa-sync-alt',
    title: 'Colocación y recaudo',
    description:
      'Controle desembolsos, ciclos de cobro, notas de crédito y conciliaciones bancarias en una sola consola.'
  },
  {
    icon: 'fas fa-cloud-upload-alt',
    title: 'Integraciones abiertas',
    description:
      'APIs y conectores listos para ERPs, banca en línea, firmas digitales y sistemas contables existentes.'
  }
];

const compliancePoints = [
  'Reportes autorizados por la Dirección General de Ingresos (DGI)',
  'Módulo de cumplimiento KYC, listas restrictivas y SARLAFT',
  'Bitácora inalterable y trazabilidad completa por operación',
  'Respaldo en la nube de Microsoft con redundancia regional'
];

const trustBadges = [
  'ISO/IEC 27001',
  'Microsoft Partner',
  'Más de 30 años de experiencia',
  'Mesa de soporte local 24/7'
];

const caseStudies = [
  {
    title: 'Financiera líder en Panamá',
    description:
      'Migró 12 sistemas heredados a TEKINS Factoring Framework y redujo en 48% el tiempo de desembolso, manteniendo la trazabilidad exigida por la Superintendencia.'
  },
  {
    title: 'Institución multilatina',
    description:
      'Unificó operaciones en 4 países con plantillas regulatorias locales sin reescribir el núcleo. Los cierres contables pasaron de días a horas.'
  },
  {
    title: 'Fintech de factoring electrónico',
    description:
      'Escaló a más de USD 1B procesados con APIs abiertas y monitoreo transaccional en vivo para sus inversionistas.'
  }
];

function Factoring() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className='factoring-page'>
      <Helmet>
        <meta charSet='utf-8' />
        <title>TEKINS Factoring Framework | Factoring inteligente y regulado</title>
        <meta
          name='description'
          content='TEKINS Factoring Framework es la plataforma SaaS líder en Panamá para administrar factoring con cumplimiento DGI, reporting avanzado y más de 30 años de evolución.'
        />
      </Helmet>

      <section className='factoring-hero'>
        <div className='factoring-hero__content'>
          <div className='factoring-hero__text'>
            <h1>Factoring inteligente, sólido y probado en el mercado</h1>
            <p>
              El sistema de factoring líder en Panamá, en evolución continua desde 1994, con reportes exigidos por la DGI y
              amplia experiencia de implementación.
            </p>
            <ul className='factoring-hero__bullets'>
              <li><i className='fas fa-check-circle' />+30 años de evolución</li>
              <li><i className='fas fa-check-circle' />Reportes autorizados por la DGI</li>
              <li><i className='fas fa-check-circle' />Más de un billón negociado a través del sistema</li>
            </ul>
            <div className='factoring-hero__actions'>
              <a className='factoring-button primary' href='mailto:info@tekins.com?subject=Demo%20Factoring'>Solicitar demo</a>
              <a className='factoring-button secondary' href='https://api.whatsapp.com/send?phone=50766435949&text=Quiero%20hablar%20sobre%20TEKINS%20Factoring'>
                Hablar con un especialista
              </a>
            </div>
            <small>Fuente: Datos internos TEKINS</small>
          </div>

          <div className='factoring-hero__visual'>
            <div className='factoring-stats'>
              <div className='factoring-stat-card'>
                <strong>+1994</strong>
                <span>Año de inicio del framework</span>
              </div>
              <div className='factoring-stat-card'>
                <strong>99.9%</strong>
                <span>Disponibilidad respaldada en la nube</span>
              </div>
              <div className='factoring-stat-card'>
                <strong>24/7</strong>
                <span>Centro de soporte especializado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='section-wrapper'>
        <h2 className='section-title'>Arquitectura modular para escalar su operación</h2>
        <p className='section-subtitle'>Seleccione las capacidades que necesita hoy y actívelas en nuevas geografías sin reescribir procesos.</p>
        <div className='module-grid'>
          {modules.map((mod) => (
            <article className='module-card' key={mod.title}>
              <span><i className={mod.icon} /></span>
              <h3>{mod.title}</h3>
              <p>{mod.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className='section-wrapper compliance-wrapper'>
        <div className='compliance-card'>
          <h3>Cumplimiento y gobierno de datos</h3>
          <p>TEKINS Factoring Framework incorpora controles alineados a mejores prácticas y marcos regulatorios panameños.</p>
          <ul>
            {compliancePoints.map((point) => (
              <li key={point}>
                <i className='fas fa-shield-alt' />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className='compliance-card'>
          <h3>Sellos de confianza</h3>
          <p>Respaldamos cada proyecto con certificaciones, experiencia y alianzas tecnológicas de primer nivel.</p>
          <div className='badge-row'>
            {trustBadges.map((badge) => (
              <span className='badge' key={badge}>{badge}</span>
            ))}
          </div>
          <Link className='factoring-button secondary' to='/Factoring/clientes'>Ver casos de éxito</Link>
        </div>
      </section>

      <section className='case-studies'>
        <div className='section-wrapper'>
          <h2 className='section-title'>Casos reales en operación</h2>
          <p className='section-subtitle'>Implementaciones que combinan factoring tradicional, confirming y soluciones de financiación estructurada.</p>
          <div className='case-grid'>
            {caseStudies.map((item) => (
              <article className='case-card' key={item.title}>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className='contact-cta'>
        <div className='cta-inner'>
          <h2>Planifiquemos la evolución de su factoring</h2>
          <p>
            Combine nuestra experiencia de más de tres décadas con un framework SaaS listo para auditar, integrar y crecer. Podemos iniciar con un piloto de pocas semanas.
          </p>
          <div className='action-row'>
            <a className='factoring-button primary' href='mailto:info@tekins.com?subject=Demo%20Factoring'>Agendar demo</a>
            <a className='factoring-button secondary' href='https://api.whatsapp.com/send?phone=50766435949&text=Quiero%20reservar%20una%20consultor%C3%ADa%20con%20TEKINS%20Factoring'>
              Reservar consultoría
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Factoring;
